import { type PlatformAdapter, type PlatformConfig, mergePlatformConfig } from '@cv-builder/core'
import { createVHStorageAdapter } from './api'
import { type VHAuthConfig, createVHAuthAdapter } from './auth'
import { VH_MESSAGES, notifyParent } from './iframe'

export interface VHAdapterConfig {
  /** Base URL for the Virtual Hive API */
  apiBaseUrl: string
  /** Auth configuration */
  auth?: VHAuthConfig
  /** Platform config overrides */
  config?: Partial<PlatformConfig>
  /**
   * Resolve the current hive id, sent as the `virtualhive-tenant` header that
   * api-service `/cvs` requires for hive scoping. Provided by the host (URL
   * param / env / parent postMessage context). Defaults to null when unset.
   */
  getHiveId?: () => Promise<string | null> | string | null
  /**
   * Base URL of the pdf-service (same-origin proxy). PDFs render with the shared
   * core renderer and post here. Defaults to '/pdf' (cv-builder-vh nginx proxies
   * it to the internal pdf-service, mirroring the demo).
   */
  pdfServiceUrl?: string
}

/**
 * Create a Virtual Hive platform adapter
 * For use when CV-Builder is embedded in the VH client-mt via iframe
 */
export function createVHAdapter(config: VHAdapterConfig): PlatformAdapter {
  const { apiBaseUrl, auth: authConfig, config: platformConfig, getHiveId, pdfServiceUrl } = config

  const authAdapter = createVHAuthAdapter(authConfig)

  const storageAdapter = createVHStorageAdapter({
    baseUrl: apiBaseUrl,
    getToken: () => authAdapter.getAccessToken(),
    getHiveId: async () => (getHiveId ? await getHiveId() : null),
    pdfServiceUrl: pdfServiceUrl ?? '/pdf',
  })

  const defaultConfig: PlatformConfig = {
    platform: 'virtual-hive',
    locale: {
      defaultUiLocale: 'da-DK',
      defaultCvLocale: 'da-DK',
      supportedLocales: ['da-DK', 'en-US'],
    },
    templates: [
      {
        id: 'classic',
        name: 'Klassisk',
        description: 'Et rent og professionelt layout',
        previewUrl: '/templates/classic.png',
      },
      {
        id: 'modern',
        name: 'Moderne',
        description: 'Nutidigt design med accentfarver',
        previewUrl: '/templates/modern.png',
      },
      {
        id: 'minimal',
        name: 'Minimal',
        description: 'Enkelt og elegant',
        previewUrl: '/templates/minimal.png',
      },
      {
        id: 'sidebar-left',
        name: 'Sidebar venstre',
        description: 'Foto og kompetencer i venstre side',
        previewUrl: '/templates/sidebar-left.png',
      },
      {
        id: 'sidebar-right',
        name: 'Sidebar højre',
        description: 'Foto og kompetencer i højre side',
        previewUrl: '/templates/sidebar-right.png',
      },
    ],
    features: {
      photoUpload: true,
      pdfExport: true,
      multipleCVs: true,
      customSections: true,
      aiAssist: false,
    },
  }

  // Notify parent that CV-Builder is ready
  setTimeout(() => {
    notifyParent(VH_MESSAGES.READY)
  }, 0)

  return {
    storage: storageAdapter,
    auth: authAdapter,
    config: mergePlatformConfig(defaultConfig, platformConfig),
  }
}

// Re-export utilities
export { createVHAuthAdapter, type VHAuthConfig } from './auth'
export { createVHStorageAdapter, type VHApiConfig } from './api'
export {
  postToParent,
  onParentMessage,
  requestFromParent,
  notifyParent,
  VH_MESSAGES,
  type VHLocaleMessagePayload,
  type VHMessage,
} from './iframe'
